// App.web.js - Fixed web version without problematic imports
import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Provider as PaperProvider } from 'react-native-paper';

// Import web-compatible components
import LoginScreenWeb from './src/screens/LoginScreen.web';
import TenderOrdersScreenWeb from './src/screens/TenderOrdersScreen.web';
import DirectOrdersScreenWeb from './src/screens/DirectOrdersScreen.web';
import OrderDetailsScreenWeb from './src/screens/OrderDetailsScreen.web';
import HistoryOrdersScreenWeb from './src/screens/HistoryOrdersScreen.web';
import WebTabNavigation from './src/components/WebTabNavigation';

// Import your REAL contexts (not mock ones)
import { AuthProvider, useAuth } from './src/context/AuthContext';
import { FirebaseProvider } from './src/context/FirebaseContext';

const theme = {
  colors: {
    primary: '#2196F3',
    accent: '#FF9800',
    background: '#f5f5f5',
    surface: '#ffffff',
    text: '#333333',
    error: '#f44336',
    success: '#4CAF50',
  },
};

// Main App Navigator for Web
function WebAppNavigator() {
  const { user, loading, isLoggingOut } = useAuth();
  const [activeTab, setActiveTab] = React.useState('tender');
  const [currentScreen, setCurrentScreen] = React.useState('main');
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [deepLinkedOrderNumber, setDeepLinkedOrderNumber] = React.useState(null);

  // Parse deep link on load
  React.useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/order\/(ORD-\d+)/);
    if (match) {
      const orderNumber = match[1];
      setDeepLinkedOrderNumber(orderNumber);
      setCurrentScreen('orderDetails');
    }
  }, []);

  // Reset navigation state when user logs out
  React.useEffect(() => {
    if (!user && !loading) {
      console.log('🔄 User logged out, resetting navigation state');
      setCurrentScreen('main');
      setSelectedOrder(null);
      setDeepLinkedOrderNumber(null);
      setActiveTab('tender');
      
      // Reset URL to root
      if (window.location.pathname !== '/') {
        window.history.replaceState({}, '', '/');
      }
    }
  }, [user, loading]);

  // Handle browser back/forward navigation
  React.useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/order\/(ORD-\d+)/);
      
      if (match) {
        const orderNumber = match[1];
        setDeepLinkedOrderNumber(orderNumber);
        setCurrentScreen('orderDetails');
      } else {
        setCurrentScreen('main');
        setSelectedOrder(null);
        setDeepLinkedOrderNumber(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigation = {
    navigate: (screenName, params) => {
      if (screenName === 'OrderDetails') {
        setSelectedOrder(params.order || null);
        setDeepLinkedOrderNumber(params.order?.orderNumber || params.orderNumber || null);
        setCurrentScreen('orderDetails');
        window.history.pushState({}, '', `/order/${params.order?.orderNumber || params.orderNumber}`);
      }
    },
    goBack: () => {
      setCurrentScreen('main');
      setSelectedOrder(null);
      setDeepLinkedOrderNumber(null);
      window.history.pushState({}, '', '/');
    }
  };

  // Show loading state during auth initialization or logout
  if (loading || isLoggingOut) {
    return (
      <View style={[styles.webContainer, styles.loadingContainer]}>
        <div style={styles.loadingSpinner}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>
            {isLoggingOut ? 'מתנתק...' : 'טוען...'}
          </div>
        </div>
      </View>
    );
  }

  // Show login screen if no user
  if (!user) {
    return (
      <View style={styles.webContainer}>
        <LoginScreenWeb />
      </View>
    );
  }

  // Show order details screen
  if (currentScreen === 'orderDetails') {
    return (
      <View style={styles.webContainer}>
        <OrderDetailsScreenWeb
          route={{
            params: selectedOrder
              ? { order: selectedOrder }
              : { orderNumber: deepLinkedOrderNumber }
          }}
          navigation={navigation}
        />
      </View>
    );
  }

  // Show main app with tabs
  return (
    <View style={styles.webContainer}>
      <WebTabNavigation 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
        user={user}
      />
      <View style={styles.webContent}>
        {activeTab === 'tender' && <TenderOrdersScreenWeb navigation={navigation} />}
        {activeTab === 'direct' && <DirectOrdersScreenWeb navigation={navigation} />}
        {activeTab === 'history' && <HistoryOrdersScreenWeb navigation={navigation} />}
      </View>
    </View>
  );
}

// Main App Component
export default function AppWeb() {
  return (
    <FirebaseProvider>
      <AuthProvider>
        <PaperProvider theme={theme}>
          <WebAppNavigator />
        </PaperProvider>
      </AuthProvider>
    </FirebaseProvider>
  );
}

const styles = StyleSheet.create({
  webContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    height: '100vh',
    width: '100vw',
  },
  webContent: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingSpinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2196F3',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#666',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
});

// Add CSS animation for spinner (this should be in your main CSS file)
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}